import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import LeadCaptureModal from "../components/LeadCaptureModal";
import api from "../services/api";
import {
  TrendingUp, Shield, Zap, Users, BarChart2, Globe,
  ArrowRight, CheckCircle, Star, ChevronRight, Lock,
  Clock, Activity, Cpu, Award, Quote, GraduationCap,
  BookOpen, MessageCircle, Phone, Send, CheckCheck
} from "lucide-react";

/* ─── Counter animation hook ────────────────────────────────────────── */
function useCountUp(target, duration = 1500, start = false) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setValue(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return value;
}

export default function MarketingLanding() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef(null);
  const [totalLeads, setTotalLeads] = useState(0);

  /* Intersection observer for stats */
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  const animatedLeads = useCountUp(12400, 2000, statsVisible);
  const animatedFormations = useCountUp(47, 1500, statsVisible);
  const animatedExperts = useCountUp(24, 1500, statsVisible);

  /* Features */
  const FEATURES = [
    { icon: TrendingUp, title: t("marketing.features.experts_title"), desc: t("marketing.features.experts_desc") },
    { icon: MessageCircle, title: t("marketing.features.whatsapp_title"), desc: t("marketing.features.whatsapp_desc") },
    { icon: Activity, title: t("marketing.features.trading_title"), desc: t("marketing.features.trading_desc") },
    { icon: Shield, title: t("marketing.features.support_title"), desc: t("marketing.features.support_desc") },
  ];

  return (
    <div className="min-h-screen bg-[#020617] text-white overflow-x-hidden" style={{ fontFamily: "'Inter', sans-serif" }}>
      <Header />

      {/* ─── HERO SECTION ──────────────────────────────────────────── */}
      <section className="relative min-h-screen flex items-center justify-center px-6 pt-28 pb-20">
        {/* Background effects */}
        <div className="absolute top-1/4 left-1/4 w-full max-w-[700px] h-[700px] bg-emerald-600/8 rounded-full blur-[180px] pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-full max-w-[600px] h-[600px] bg-indigo-600/8 rounded-full blur-[180px] pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-[900px] h-[900px] bg-emerald-500/3 rounded-full blur-[200px] pointer-events-none" />
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)", backgroundSize: "60px 60px" }} />

        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-300 text-xs font-bold uppercase tracking-[0.15em] mb-10 backdrop-blur-sm">
            <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
            {t("marketing.hero.badge")}
          </div>

          <h1 className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tight leading-[1.02] mb-8" style={{ fontFamily: "'Outfit', sans-serif" }}>
            <span className="block text-white">{t("marketing.hero.title_line1")}</span>
            <span className="block bg-gradient-to-r from-emerald-400 via-teal-400 to-indigo-400 bg-clip-text text-transparent">
              {t("marketing.hero.title_line2")}
            </span>
          </h1>

          <p className="text-slate-400 text-lg md:text-xl font-medium max-w-3xl mx-auto mb-10 leading-relaxed">
            {t("marketing.hero.subtitle")}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-14">
            <button
              onClick={() => setShowModal(true)}
              className="group px-10 py-5 bg-emerald-500 hover:bg-emerald-400 rounded-2xl font-black text-sm uppercase tracking-[0.12em] transition-all duration-200 shadow-2xl shadow-emerald-500/30 hover:shadow-emerald-500/50 flex items-center justify-center gap-3 active:scale-95"
            >
              {t("marketing.hero.cta_primary")}
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-10 py-5 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded-2xl font-black text-sm uppercase tracking-[0.12em] transition-all duration-200 flex items-center justify-center gap-3 backdrop-blur-sm"
            >
              <GraduationCap size={18} />
              {t("marketing.hero.cta_secondary")}
            </button>
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap justify-center gap-6">
            {[
              { icon: MessageCircle, label: t("marketing.hero.badge_whatsapp") },
              { icon: Shield, label: t("marketing.hero.badge_experts") },
              { icon: Award, label: t("marketing.hero.badge_certificate") },
              { icon: Activity, label: t("marketing.hero.badge_simulator") },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase tracking-wider">
                <Icon size={13} className="text-emerald-500" />
                {label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── STATS SECTION ─────────────────────────────────────────── */}
      <section ref={statsRef} className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: t("marketing.stats.trained"), value: animatedLeads, suffix: "+", icon: Users },
            { label: t("marketing.stats.programs"), value: animatedFormations, suffix: "+", icon: BookOpen },
            { label: t("marketing.stats.experts"), value: animatedExperts, suffix: "", icon: Award },
            { label: t("marketing.stats.satisfaction"), value: 98, suffix: "%", icon: Star },
          ].map(({ label, value, suffix, icon: Icon }) => (
            <div key={label} className="group p-7 bg-white/[0.02] border border-white/5 rounded-2xl text-center hover:border-emerald-500/20 hover:bg-white/[0.04] transition-all duration-300">
              <Icon size={22} className="text-emerald-400 mx-auto mb-4 group-hover:scale-110 transition-transform" />
              <div className="text-3xl font-black text-white mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {value}{suffix}
              </div>
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.15em]">{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── FEATURES SECTION ──────────────────────────────────────── */}
      <section id="features" className="max-w-6xl mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-indigo-500/10 border border-indigo-500/20 rounded-full text-indigo-400 text-xs font-bold uppercase tracking-widest mb-5">
            <Zap size={11} />
            {t("marketing.features.section_badge")}
          </div>
          <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {t("marketing.features.section_title")}{" "}
            <span className="bg-gradient-to-r from-indigo-400 to-emerald-400 bg-clip-text text-transparent">
              {t("marketing.features.section_title_highlight")}
            </span>
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {FEATURES.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="group p-8 bg-white/[0.02] border border-white/5 rounded-3xl hover:border-indigo-500/20 hover:bg-white/[0.04] transition-all duration-300">
              <div className="relative w-14 h-14 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400 mb-6 group-hover:scale-110 transition-transform duration-300">
                <Icon size={26} />
              </div>
              <h3 className="relative text-xl font-black mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>{title}</h3>
              <p className="relative text-slate-400 font-medium leading-relaxed text-sm">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ─── HOW IT WORKS ──────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {t("marketing.how.title")}{" "}
            <span className="bg-gradient-to-r from-emerald-400 to-indigo-400 bg-clip-text text-transparent">
              {t("marketing.how.title_highlight")}
            </span>
          </h2>
        </div>

        <div className="grid md:grid-cols-4 gap-6">
          {[
            { step: "1", title: t("marketing.how.step1_title"), desc: t("marketing.how.step1_desc"), icon: Users },
            { step: "2", title: t("marketing.how.step2_title"), desc: t("marketing.how.step2_desc"), icon: CheckCircle },
            { step: "3", title: t("marketing.how.step3_title"), desc: t("marketing.how.step3_desc"), icon: MessageCircle },
            { step: "4", title: t("marketing.how.step4_title"), desc: t("marketing.how.step4_desc"), icon: Award },
          ].map(({ step, title, desc, icon: Icon }) => (
            <div key={step} className="group p-8 bg-white/[0.02] border border-white/5 rounded-3xl text-center hover:border-emerald-500/20 transition-all duration-300">
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 text-2xl font-black mb-6 group-hover:scale-110 transition-transform">
                {step}
              </div>
              <Icon size={24} className="text-emerald-400 mx-auto mb-4" />
              <h3 className="text-lg font-black mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{title}</h3>
              <p className="text-slate-400 text-sm font-medium">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ─── TESTIMONIALS ──────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {t("marketing.testimonials.title")}{" "}
            <span className="bg-gradient-to-r from-emerald-400 to-indigo-400 bg-clip-text text-transparent">
              {t("marketing.testimonials.title_highlight")}
            </span>
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {[
            { name: t("marketing.testimonials.user1_name"), role: t("marketing.testimonials.user1_role"), content: t("marketing.testimonials.user1_content"), rating: 5 },
            { name: t("marketing.testimonials.user2_name"), role: t("marketing.testimonials.user2_role"), content: t("marketing.testimonials.user2_content"), rating: 5 },
            { name: t("marketing.testimonials.user3_name"), role: t("marketing.testimonials.user3_role"), content: t("marketing.testimonials.user3_content"), rating: 5 },
          ].map(({ name, role, content, rating }) => (
            <div key={name} className="p-8 bg-white/[0.02] border border-white/5 rounded-3xl hover:border-emerald-500/20 hover:bg-white/[0.04] transition-all duration-300">
              <Quote size={28} className="text-emerald-500/40 mb-5" />
              <div className="flex gap-1 mb-5">
                {Array.from({ length: rating }).map((_, i) => (
                  <Star key={i} size={14} className="text-amber-400 fill-amber-400" />
                ))}
              </div>
              <p className="text-slate-300 text-sm font-medium leading-relaxed mb-7">"{content}"</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-300 text-xs font-black">
                  {name.charAt(0)}
                </div>
                <div>
                  <div className="text-sm font-bold text-white">{name}</div>
                  <div className="text-xs text-slate-500 font-medium">{role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── CTA FINAL ─────────────────────────────────────────────── */}
      <section className="max-w-4xl mx-auto px-6 py-24 text-center">
        <div className="relative p-14 rounded-3xl overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 via-teal-500/10 to-indigo-600/10 rounded-3xl" />
          <div className="absolute inset-0 border border-emerald-500/20 rounded-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-emerald-500/10 rounded-full blur-[100px]" />

          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-xs font-bold uppercase tracking-widest mb-8">
              <Clock size={11} />
              {t("marketing.cta.badge")}
            </div>
            <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {t("marketing.cta.title")}
            </h2>
            <p className="text-slate-400 font-medium text-lg mb-12 max-w-lg mx-auto">
              {t("marketing.cta.subtitle")}
            </p>
            <button
              onClick={() => setShowModal(true)}
              className="group px-12 py-5 bg-emerald-500 hover:bg-emerald-400 rounded-2xl font-black text-sm uppercase tracking-[0.12em] transition-all duration-200 shadow-2xl shadow-emerald-500/40 hover:shadow-emerald-500/60 inline-flex items-center gap-3 active:scale-95"
            >
              {t("marketing.cta.button")}
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>

            <div className="flex flex-wrap justify-center gap-6 mt-10">
              {[
                { icon: MessageCircle, label: t("marketing.cta.response_time") },
                { icon: Shield, label: t("marketing.cta.no_commitment") },
                { icon: Award, label: t("marketing.cta.certificate") },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-2 text-slate-500 text-xs font-semibold">
                  <Icon size={13} className="text-emerald-500" />
                  {label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* ─── LEAD CAPTURE MODAL ────────────────────────────────────── */}
      {showModal && <LeadCaptureModal onClose={() => setShowModal(false)} />}
    </div>
  );
}
